import { BarcodeMetadataItem } from 'types';

/**
 * A parameter is "critical" (affects the overall grade) when CTF classifies it as ISO or NonISO.
 * Everything else (Informational, or legacy data with no contribution) is supplementary.
 */
export const isCriticalContribution = (contribution?: string): boolean =>
  contribution === 'ISO' || contribution === 'NonISO';

export interface PartitionedBarcodeMetadata {
  /** Type + grade rows, always shown at the top. */
  primary: BarcodeMetadataItem[];
  /** ISO/NonISO rows (only populated when CTF classified the parameters). */
  critical: BarcodeMetadataItem[];
  /** Informational rows, plus all secondary rows for legacy (unclassified) data. */
  supplementary: BarcodeMetadataItem[];
  /** Whether any secondary parameter carries a CTF contribution; gates the new segmented layout. */
  hasContributionData: boolean;
  /** Raw passingStatus of the overall grade ("Pass" | "Warning" | "Fail" | ...). */
  overallPassingStatus?: string;
}

/**
 * Splits the flat barcode metadata coming from the API into the groups the panel renders.
 *
 * - `humanReadable`/`resolvedUrl` rows are dropped (their *Status counterparts are shown instead).
 * - `barcodeType` and `grade` go to `primary`.
 * - When CTF has classified the parameters (`contribution` present), the rest is split into
 *   `critical` (ISO/NonISO) and `supplementary` (Informational).
 * - For legacy inspections with no `contribution`, everything secondary stays in `supplementary`
 *   so nothing is mislabeled, and `hasContributionData` is false (caller keeps the flat layout).
 */
export const partitionBarcodeMetadata = (
  barcodeMetadata: BarcodeMetadataItem[] | undefined,
): PartitionedBarcodeMetadata => {
  const filtered = (barcodeMetadata || []).filter((item) => item.key !== 'humanReadable' && item.key !== 'resolvedUrl');

  const primary: BarcodeMetadataItem[] = [];
  const secondary: BarcodeMetadataItem[] = [];
  for (const item of filtered) {
    if (item.key === 'barcodeType' || item.key === 'grade') {
      primary.push(item);
    } else {
      secondary.push(item);
    }
  }

  const gradeItem = primary.find((item) => item.key === 'grade');
  const hasContributionData = secondary.some((item) => isCriticalContribution(item.contribution));
  const critical = hasContributionData ? secondary.filter((item) => isCriticalContribution(item.contribution)) : [];
  const supplementary = hasContributionData
    ? secondary.filter((item) => !isCriticalContribution(item.contribution))
    : secondary;

  return {
    primary,
    critical,
    supplementary,
    hasContributionData,
    overallPassingStatus: gradeItem?.passingStatus,
  };
};
